/**
 * React Query mutation hook for natural-language → structured rule
 * translation.
 *
 * Unlike the CRUD mutations in this directory, translation never touches
 * the cache: it is a pure function on input, and the admin explicitly
 * decides whether to persist the result. The hook wraps the service call
 * so the component config page can reuse TanStack's loading / error
 * state machine instead of rolling its own.
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import {
  componentRulesService,
  type TranslateComponentRulePayload,
  type TranslateComponentRuleResponse,
} from '../../api/componentRulesService';

/**
 * Mutation hook for `POST /claimflow/component-rules/translate`.
 *
 * No cache invalidation — translation is idempotent and does not
 * persist anything the frontend cares about (the server still writes an
 * audit-log row, which is a separate query).
 */
export function useTranslateComponentRule(): UseMutationResult<
  TranslateComponentRuleResponse,
  Error,
  TranslateComponentRulePayload
> {
  return useMutation({
    mutationFn: (payload: TranslateComponentRulePayload) =>
      componentRulesService.translateRule(payload),
  });
}
